import { useEffect, useRef, useState } from "react";

/**
 * Canvas-based particle headline.
 * Particles fly in from random positions, form the letters, then the canvas
 * cross-fades out while the real solid text fades in — leaving crisp typography.
 * A visually-hidden <h1> is preserved in the DOM for SEO / accessibility.
 */
export default function ParticleHeadline({
  lines,
  fontSizeCss = "clamp(44px, 7.4vw, 96px)",
  className = "",
  onReady,
}) {
  const wrapRef = useRef(null);
  const canvasRef = useRef(null);
  const rafRef = useRef(0);
  const settledRef = useRef(false);
  const [settled, setSettled] = useState(false);

  useEffect(() => {
    let cancelled = false;

    const start = async () => {
      // Wait for the display font so measured metrics match painted metrics
      if (document.fonts && document.fonts.ready) {
        try {
          await document.fonts.load(`900 96px "Archivo Black"`);
          await document.fonts.ready;
        } catch (e) {
          /* ignore */
        }
      }
      if (cancelled) return;
      run();
    };

    const run = () => {
      const wrap = wrapRef.current;
      const canvas = canvasRef.current;
      if (!wrap || !canvas) return;

      const rect = wrap.getBoundingClientRect();
      const dpr = Math.min(window.devicePixelRatio || 1, 2);

      canvas.width = Math.max(1, Math.floor(rect.width * dpr));
      canvas.height = Math.max(1, Math.floor(rect.height * dpr));
      canvas.style.width = rect.width + "px";
      canvas.style.height = rect.height + "px";

      const ctx = canvas.getContext("2d");
      ctx.setTransform(dpr, 0, 0, dpr, 0, 0);
      ctx.clearRect(0, 0, rect.width, rect.height);

      // Sampling canvas (offscreen)
      const off = document.createElement("canvas");
      off.width = canvas.width;
      off.height = canvas.height;
      const octx = off.getContext("2d");
      octx.setTransform(dpr, 0, 0, dpr, 0, 0);

      // Paint each line using the DOM element's actual computed metrics
      const els = wrap.querySelectorAll("[data-particle-line]");
      const wrapTop = rect.top;
      const wrapLeft = rect.left;

      els.forEach((el, i) => {
        const lr = el.getBoundingClientRect();
        const cs = getComputedStyle(el);
        const line = lines[i] || lines[0];
        octx.font = `${cs.fontWeight} ${cs.fontSize}/${cs.lineHeight} ${cs.fontFamily}`;
        octx.textAlign = "center";
        octx.textBaseline = "top";
        octx.fillStyle = line.color;
        octx.globalAlpha = line.opacity ?? 1;
        const cx = lr.left - wrapLeft + lr.width / 2;
        const cy = lr.top - wrapTop;
        octx.fillText(line.text, cx, cy);
      });

      const w = off.width;
      const h = off.height;
      const img = octx.getImageData(0, 0, w, h).data;

      // Sample particles from opaque pixels
      const step = Math.max(3, Math.round(3 * dpr));
      const particles = [];
      for (let y = 0; y < h; y += step) {
        for (let x = 0; x < w; x += step) {
          const idx = (y * w + x) * 4;
          const a = img[idx + 3];
          if (a > 90) {
            const tx = x / dpr;
            const ty = y / dpr;
            // Origin: scattered around the whole hero region, biased outward
            const angle = Math.random() * Math.PI * 2;
            const dist = 220 + Math.random() * Math.max(rect.width, 600);
            const ox = rect.width / 2 + Math.cos(angle) * dist;
            const oy = rect.height / 2 + Math.sin(angle) * dist * 0.6;
            particles.push({
              tx,
              ty,
              ox,
              oy,
              r: img[idx],
              g: img[idx + 1],
              b: img[idx + 2],
              a: a / 255,
              delay: Math.random() * 480,
              // Idle jitter phase + amplitude
              ph: Math.random() * Math.PI * 2,
              amp: 0.35 + Math.random() * 0.6,
            });
          }
        }
      }

      // Reveal a11y H1 as soon as particles finish forming
      const t0 = performance.now();
      const duration = 1500;
      const totalDone = duration + 480;

      const tick = (now) => {
        const elapsed = now - t0;
        ctx.clearRect(0, 0, rect.width, rect.height);
        // Additive blend for slight glow on gold
        ctx.globalCompositeOperation = "lighter";

        for (let i = 0; i < particles.length; i++) {
          const p = particles[i];
          const local = Math.max(0, Math.min(1, (elapsed - p.delay) / duration));
          // ease-out cubic
          const e = 1 - Math.pow(1 - local, 3);

          const x = p.ox + (p.tx - p.ox) * e;
          const y = p.oy + (p.ty - p.oy) * e;

          // Fade in as particles approach target
          const alpha = p.a * (0.15 + 0.85 * e);
          ctx.fillStyle = `rgba(${p.r},${p.g},${p.b},${alpha})`;
          // Slightly larger square at destination for legibility
          const s = 1 + e * 0.4;
          ctx.fillRect(x, y, s, s);
        }
        ctx.globalCompositeOperation = "source-over";

        if (elapsed >= totalDone) {
          if (!settledRef.current) {
            settledRef.current = true;
            setSettled(true);
            if (onReady) onReady();
          }
          return; // stop RAF — solid text takes over via CSS crossfade
        }
        rafRef.current = requestAnimationFrame(tick);
      };

      rafRef.current = requestAnimationFrame(tick);
    };

    start();

    // Redraw on resize (debounced)
    let rt;
    const onResize = () => {
      clearTimeout(rt);
      rt = setTimeout(() => {
        cancelAnimationFrame(rafRef.current);
        run();
      }, 220);
    };
    window.addEventListener("resize", onResize);

    return () => {
      cancelled = true;
      cancelAnimationFrame(rafRef.current);
      window.removeEventListener("resize", onResize);
      clearTimeout(rt);
    };
  }, [lines, onReady]);

  const a11yText = lines.map((l) => l.text).join(" ");

  return (
    <div ref={wrapRef} className={`relative w-full ${className}`}>
      {/* SEO/a11y semantic heading — visually hidden */}
      <h1 className="sr-only">{a11yText}</h1>

      {/* Solid text — fades in when particles finish. Reserves layout at all times. */}
      <div
        aria-hidden
        className="font-display max-w-[1400px] mx-auto"
        style={{
          fontSize: fontSizeCss,
          lineHeight: 0.9,
          letterSpacing: "-0.035em",
          opacity: settled ? 1 : 0,
          transition: "opacity 600ms cubic-bezier(0.16, 1, 0.3, 1)",
        }}
      >
        {lines.map((line, i) => (
          <div
            key={line.text + i}
            data-particle-line
            style={{
              marginBottom: i < lines.length - 1 ? "-0.05em" : 0,
              color: line.color,
              opacity: line.opacity ?? 1,
            }}
          >
            {line.text}
          </div>
        ))}
      </div>

      {/* Particle canvas overlay — fades out when solid text appears */}
      <canvas
        ref={canvasRef}
        aria-hidden
        className="absolute inset-0 pointer-events-none"
        style={{
          opacity: settled ? 0 : 1,
          transition: "opacity 500ms cubic-bezier(0.16, 1, 0.3, 1)",
        }}
      />
    </div>
  );
}
